/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import Swal from "sweetalert2";
import { createTenantOrder, getCurrentCustomer } from "./storeApi";

const asText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    return String(value.hex || value.hexCode || value.name || "").trim();
  }
  const text = String(value).trim();
  return text === "[object Object]" ? "" : text;
};

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const cityIdOf = (city) => Number(city?.cityId ?? city?.CityId ?? 0);

const findCity = (cities, cityId) =>
  (Array.isArray(cities) ? cities : []).find((c) => cityIdOf(c) === Number(cityId));

const readStoredCoupon = () => {
  try {
    return JSON.parse(localStorage.getItem("storeCoupon") || "null");
  } catch {
    return null;
  }
};

export const showStoreAlert = (options) =>
  Swal.fire({
    heightAuto: false,
    confirmButtonColor: "#5B2C6F",
    ...options,
    customClass: {
      ...(options.customClass || {}),
      container: "store-swal-on-top",
    },
  });

export async function submitStoreCheckout({
  tenantId,
  cart = [],
  cities = [],
  deliveryForm = {},
  deliveryInfo = null,
  subtotal = 0,
  total = 0,
  couponDiscount = 0,
}) {
  try {
    if (!tenantId || Number(tenantId) <= 0) {
      return { ok: false, title: "خطأ", error: "معرف المتجر غير موجود" };
    }

    if (!Array.isArray(cart) || cart.length === 0) {
      return { ok: false, title: "السلة فارغة", error: "أضف منتجاً إلى السلة أولاً" };
    }

    const selectedCityId = asNumber(deliveryForm.cityId ?? deliveryForm.CityId, 0);
    if (!selectedCityId) {
      return { ok: false, title: "المحافظة مطلوبة", error: "يرجى اختيار المحافظة" };
    }

    const city = findCity(cities, selectedCityId);
    if (!city) {
      return { ok: false, title: "المحافظة غير صحيحة", error: "يرجى اختيار المحافظة من القائمة" };
    }

    const storedCustomer = getCurrentCustomer();
    const customerId = asNumber(
      storedCustomer?.customerId || localStorage.getItem("storeCustomerId"),
      0
    );
    if (!customerId) {
      return { ok: false, needAuth: true, title: "تسجيل الدخول", error: "يجب تسجيل الدخول أولاً" };
    }

    const finalCustomerName = asText(deliveryForm.name) || asText(storedCustomer?.name);
    const finalCustomerPhone = asText(deliveryForm.phone) || asText(storedCustomer?.phone);
    if (!finalCustomerName || !finalCustomerPhone) {
      return { ok: false, title: "بيانات ناقصة", error: "يرجى إدخال الاسم ورقم الهاتف" };
    }

    const updatedCustomer = {
      ...(storedCustomer || {}),
      customerId,
      name: finalCustomerName,
      phone: finalCustomerPhone,
      address: asText(deliveryForm.address) || asText(storedCustomer?.address),
      email: asText(deliveryForm.email) || asText(storedCustomer?.email),
      cityId: selectedCityId,
      cityName: asText(city.cityName || city.CityName || storedCustomer?.cityName),
    };

    localStorage.setItem("storeCustomer", JSON.stringify(updatedCustomer));
    localStorage.setItem("storeCustomerId", String(customerId));

    const orderId =
      localStorage.getItem("currentOrderId") ||
      cart[0]?.orderId ||
      cart[0]?.OrderId ||
      "";

    const orderObj = {
      orderId,
      customerName: finalCustomerName,
      customerPhone1: finalCustomerPhone,
      address: updatedCustomer.address,
      cityId: selectedCityId,
      city: updatedCustomer.cityName,
      items: cart.map((item) => {
        const qty = asNumber(item.quantity ?? item.qty, 0);
        const price = asNumber(item.price ?? item.unitPrice, 0);
        return {
          itemId: asNumber(item.itemId, 0),
          variantId: asNumber(item.variantId, 0),
          itemName: asText(item.name || item.itemName),
          variantName: asText(item.variantName),
          colorName: asText(item.colorHex || item.colorName || item.color || item.hex),
          size: asText(item.size),
          quantity: qty,
          price,
          imageUrl: asText(item.imageUrl || item.image),
          stockQty: asNumber(item.stockQty, 0),
          discount: asNumber(item.discount, 0),
          lineTotal: price * qty,
          costPrice: asNumber(item.costPrice, 0),
        };
      }),
      totalAmount: asNumber(subtotal, 0),
      netAmount: asNumber(total, 0),
      discountAmount: asNumber(couponDiscount, 0),
      deliveryId: asNumber(deliveryInfo?.deliveryId, 0),
    };

    const result = await createTenantOrder(
      tenantId,
      {
        ...city,
        cityId: cityIdOf(city),
        cityName: asText(city.cityName || city.CityName),
      },
      orderObj,
      readStoredCoupon(),
      updatedCustomer
    );

    if (result?.success) {
      return { ok: true, data: result.data, orderId };
    }

    return {
      ok: false,
      title: "فشل إرسال الطلب",
      error: result?.error || "فشل إرسال الطلب",
    };
  } catch (err) {
    console.error("Checkout Error:", err);
    return {
      ok: false,
      title: "خطأ تقني",
      error: err?.message || "خطأ تقني أثناء إرسال الطلب",
    };
  }
}
